import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";
import Flourish from "../components/Flourish";
import Framed from "../components/Framed";
import Marquee from "../components/Marquee";
import Showroom from "../components/Showroom";
import SignatureCollection from "../components/SignatureCollection";
import {
  brand,
  collections,
  features,
  feature,
  crafts,
  heritageStats,
  tradePoints,
  lookbookMarquee,
  social,
} from "../data/site";

// Simple line icons for the Import & Export section.
function TradeIcon({ id }) {
  const common = {
    viewBox: "0 0 24 24",
    className: "h-6 w-6",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  if (id === "export")
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3c2.5 2.7 3.8 5.7 3.8 9S14.5 18.3 12 21c-2.5-2.7-3.8-5.7-3.8-9S9.5 5.7 12 3Z" />
      </svg>
    );
  if (id === "trade")
    return (
      <svg {...common}>
        <path d="M3 10.5 7 7l4 3 3-2 4 3.5" />
        <path d="M8.5 14.5 11 17l2-1.5 2 1.5 2.5-2.5" />
        <path d="M3 10.5V17a1 1 0 0 0 1 1h2M21 11.5V17a1 1 0 0 1-1 1h-2" />
      </svg>
    );
  if (id === "secure")
    return (
      <svg {...common}>
        <path d="M12 3.5 19 6v5.5c0 4.2-2.9 7.6-7 9-4.1-1.4-7-4.8-7-9V6l7-2.5Z" />
        <path d="M9 12l2.2 2.2L15.5 10" />
      </svg>
    );
  return (
    <svg {...common}>
      <path d="M8 3h8l3.5 5.5L12 21 4.5 8.5 8 3Z" />
      <path d="M4.5 8.5h15M9.5 8.5 12 21M14.5 8.5 12 21M8 3l4 5.5L16 3" />
    </svg>
  );
}

export default function Home() {
  return (
    <>
      {/* ===== Hero (split: gilt copy + full portrait) ===== */}
      <section className="grid min-h-screen lg:grid-cols-[1fr_1.05fr]">
        {/* Copy panel */}
        <div className="velvet grain relative order-2 flex items-center border-gold/20 py-20 lg:order-1 lg:border-r lg:py-0">
          <div className="relative z-10 mx-auto w-full max-w-xl px-6 text-cream md:px-12">
            <div className="mb-7 flex items-center gap-4 animate-fadeUp">
              <span className="h-px w-10 bg-gold" />
              <p className="font-sans text-xs uppercase tracking-luxe text-gold-light">
                {brand.legalName}
              </p>
            </div>
            <h1 className="font-serif text-5xl font-light leading-[1.04] sm:text-6xl lg:text-7xl">
              Heritage Gold,
              <span className="mt-1 block italic text-gilt text-gilt-anim">
                Worn Today
              </span>
            </h1>
            <p className="mt-8 max-w-md font-sans text-sm font-light leading-relaxed tracking-wide text-cream/85 md:text-base">
              Hand-crafted antique gold jewellery from our Ahmedabad atelier —
              kundan, temple and jadau heirlooms designed to be worn now and
              treasured for generations.
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Link to="/collections" className="btn-solid">
                Explore Collections
              </Link>
              <Link
                to="/contact"
                className="font-sans text-xs uppercase tracking-[0.25em] text-cream/85 underline-offset-8 transition-colors hover:text-gold-light hover:underline"
              >
                Book a Private Viewing
              </Link>
            </div>
          </div>
        </div>

        {/* Image panel */}
        <div className="relative order-1 min-h-[58vh] overflow-hidden lg:order-2 lg:min-h-screen">
          <img
            src={feature.heroHome}
            alt="Culture Antique fine antique gold jewellery"
            className="absolute inset-0 h-full w-full animate-slowZoom object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/50 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/40 to-transparent" />
          {/* thin inner gilt frame */}
          <span className="pointer-events-none absolute inset-4 border border-gold/25 md:inset-6" />
        </div>
      </section>

      {/* ===== Legacy ===== */}
      <section className="parchment py-16 md:py-28">
        <div className="container-luxe grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <Framed>
              <img
                src={feature.legacy}
                alt="Culture Antique craftsmanship"
                className="h-[440px] w-full object-cover sm:h-[540px] lg:h-[620px]"
              />
            </Framed>
          </Reveal>

          <Reveal delay={150}>
            <Flourish label="The House" className="mb-6 justify-start" />
            <h2 className="section-title mb-8">
              A legacy cast in <span className="italic text-gilt">gold</span>
            </h2>
            <div className="space-y-5 font-sans text-base font-light leading-relaxed text-charcoal/70">
              <p className="drop-cap">
                Culture Antique is a house devoted to the timeless art of antique
                gold jewellery. Each piece is conceived as an heirloom — a quiet
                statement of taste, meant to be treasured and passed down.
              </p>
              <p>
                From intricate temple motifs to uncut kundan and hand-worked gold,
                every creation is finished by master karigars in the traditions of
                fine antique craftsmanship. No two pieces are ever quite alike.
              </p>
            </div>
            <Link to="/about" className="btn-gold mt-10">
              Our Story
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ===== Heritage marks (velvet) ===== */}
      <section className="grain relative overflow-hidden">
        <div className="velvet">
          <div className="container-luxe py-16 md:py-20">
            <div className="grid gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
              {heritageStats.map((s, i) => (
                <Reveal key={s.label} delay={i * 100} className="text-center">
                  <div className="font-serif text-5xl font-light text-gilt md:text-6xl">
                    {s.value}
                  </div>
                  <p className="mx-auto mt-3 max-w-[12rem] font-sans text-xs uppercase tracking-[0.2em] text-cream/60">
                    {s.label}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== Collections ===== */}
      <section className="bg-white py-16 md:py-28">
        <div className="container-luxe">
          <Reveal className="mb-12 flex flex-col items-center text-center md:mb-16">
            <Flourish label="The Collections" className="mb-6" />
            <h2 className="section-title">Curated for every occasion</h2>
          </Reveal>

          <div className="grid gap-6 md:grid-cols-3">
            {collections.map((c, i) => (
              <Reveal key={c.id} delay={i * 120}>
                <Link to="/collections" className="group block">
                  <Framed>
                    <img
                      src={c.image}
                      alt={c.name}
                      className="h-[400px] w-full object-cover transition-transform duration-[1400ms] group-hover:scale-110 sm:h-[480px]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/15 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-7 text-cream">
                      <h3 className="font-serif text-3xl font-light">{c.name}</h3>
                      <p className="mt-2 max-w-xs font-sans text-sm font-light leading-relaxed text-cream/80">
                        {c.blurb}
                      </p>
                      <span className="mt-4 inline-block font-sans text-xs uppercase tracking-[0.25em] text-gold-light opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                        Discover →
                      </span>
                    </div>
                  </Framed>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== The Craft ===== */}
      <section className="parchment border-y border-gold/15 py-16 md:py-28">
        <div className="container-luxe">
          <Reveal className="mb-12 flex flex-col items-center text-center md:mb-16">
            <Flourish label="The Craft" className="mb-6" />
            <h2 className="section-title">Kept alive by hand</h2>
            <p className="mt-5 max-w-xl font-sans text-sm font-light leading-relaxed text-charcoal/60 md:text-base">
              Three traditions, centuries in the making, practised in our atelier
              exactly as they always have been.
            </p>
          </Reveal>

          <div className="grid gap-8 md:grid-cols-3">
            {crafts.map((c, i) => (
              <Reveal key={c.id} delay={i * 120}>
                <article className="group text-center">
                  <div className="relative mx-auto overflow-hidden rounded-t-full">
                    <img
                      src={c.image}
                      alt={c.name}
                      loading="lazy"
                      className="aspect-[3/4] w-full object-cover transition-transform duration-[1400ms] group-hover:scale-105"
                    />
                    <div className="absolute inset-0 rounded-t-full ring-1 ring-inset ring-gold/30" />
                  </div>
                  <h3 className="mt-6 font-serif text-2xl font-light italic text-charcoal">
                    {c.name}
                  </h3>
                  <div className="mx-auto my-3 h-px w-10 bg-gold/50" />
                  <p className="mx-auto max-w-xs font-sans text-sm font-light leading-relaxed text-charcoal/60">
                    {c.text}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Signature antique gold panels ===== */}
      <SignatureCollection />

      {/* ===== The Lookbook ===== */}
      <section className="velvet grain relative overflow-hidden py-16 md:py-28">
        <div className="container-luxe relative z-10">
          <Reveal className="mb-12 flex flex-col items-center text-center">
            <Flourish label="The Lookbook" className="mb-6" />
            <h2 className="section-title text-cream">Worn beautifully</h2>
            <p className="mt-5 max-w-xl font-sans text-sm font-light leading-relaxed text-cream/60 md:text-base">
              An editorial celebration of our jewellery, styled and photographed as
              it is meant to be seen.
            </p>
          </Reveal>

        </div>

        <div className="relative z-10 space-y-4">
          <Marquee>
            {lookbookMarquee.top.map((src, i) => (
              <Link
                key={src + i}
                to="/gallery"
                className="mx-2 block h-56 w-40 shrink-0 overflow-hidden md:h-72 md:w-52"
              >
                <img
                  src={src}
                  alt="Culture Antique lookbook"
                  loading="lazy"
                  className="h-full w-full object-cover object-top transition-transform duration-700 hover:scale-105"
                />
              </Link>
            ))}
          </Marquee>
          <Marquee reverse>
            {lookbookMarquee.bottom.map((src, i) => (
              <Link
                key={src + i}
                to="/gallery"
                className="mx-2 block h-56 w-40 shrink-0 overflow-hidden md:h-72 md:w-52"
              >
                <img
                  src={src}
                  alt="Culture Antique lookbook"
                  loading="lazy"
                  className="h-full w-full object-cover object-top transition-transform duration-700 hover:scale-105"
                />
              </Link>
            ))}
          </Marquee>
        </div>

        <div className="relative z-10 mt-14 text-center">
          <Link to="/gallery" className="btn-ghost">
            View the Full Lookbook
          </Link>
        </div>
      </section>

      {/* ===== Editorial philosophy ===== */}
      <section className="relative overflow-hidden">
        <div className="grid lg:grid-cols-2">
          <div className="relative min-h-[420px] lg:min-h-[640px]">
            <img
              src={feature.editorialDark}
              alt="Culture Antique editorial"
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
          <div className="flex items-center bg-charcoal px-8 py-16 text-cream md:px-16 lg:py-0">
            <Reveal>
              <span className="mb-4 block font-serif text-6xl leading-none text-gold/50">
                &ldquo;
              </span>
              <blockquote className="font-serif text-3xl font-light italic leading-snug text-cream md:text-4xl lg:text-5xl">
                Jewellery is memory made to last — a story you wear, and one day,
                pass on.
              </blockquote>
              <p className="mt-8 max-w-md font-sans text-sm font-light leading-relaxed text-cream/70">
                Every Culture Antique creation begins with that belief. We design
                not for a season, but for a lifetime — and the generations beyond.
              </p>
              <a
                href={social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-block font-sans text-xs uppercase tracking-[0.25em] text-gold-light underline-offset-8 hover:underline"
              >
                @culture_antique_jewellery
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== Features ===== */}
      <section className="parchment border-y border-gold/15 py-16 md:py-20">
        <div className="container-luxe grid gap-12 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f, i) => (
            <Reveal key={f.title} delay={i * 100}>
              <div className="hairline mb-6" />
              <h3 className="mb-3 font-serif text-2xl font-light text-charcoal">
                {f.title}
              </h3>
              <p className="font-sans text-sm font-light leading-relaxed text-charcoal/60">
                {f.text}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ===== Import & Export — Worldwide ===== */}
      <section className="velvet grain relative overflow-hidden py-16 md:py-24">
        <div className="container-luxe relative z-10">
          <Reveal className="mb-12 flex flex-col items-center text-center md:mb-14">
            <Flourish label="Import &amp; Export" className="mb-6" />
            <h2 className="section-title text-cream">
              From Ahmedabad, <span className="italic text-gilt">worldwide</span>
            </h2>
            <p className="mt-5 max-w-2xl font-sans text-sm font-light leading-relaxed text-cream/65 md:text-base">
              Our antique gold travels far beyond our showroom. We import and export
              hand-crafted jewellery for private clients, retailers and trade
              partners across the globe — packed, insured and delivered with the
              same care with which it was made.
            </p>
          </Reveal>

          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {tradePoints.map((t, i) => (
              <Reveal key={t.id} delay={i * 100} className="text-center">
                <span className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-gold/40 text-gold">
                  <TradeIcon id={t.id} />
                </span>
                <h3 className="font-serif text-xl font-medium text-gilt">{t.title}</h3>
                <div className="mx-auto my-3 h-px w-8 bg-gold/50" />
                <p className="mx-auto max-w-xs font-sans text-sm font-light leading-relaxed text-cream/60">
                  {t.text}
                </p>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/contact" className="btn-ghost">
              Trade &amp; Export Enquiries
            </Link>
          </div>
        </div>
      </section>

      {/* ===== Showroom ===== */}
      <Showroom />

      {/* ===== CTA ===== */}
      <CtaBand />
    </>
  );
}

function CtaBand() {
  return (
    <section className="grain relative overflow-hidden py-24 md:py-32">
      <img
        src={feature.cta}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-charcoal/85" />
      <div className="container-luxe relative z-10 flex flex-col items-center text-center text-cream">
        <Flourish className="mb-7" />
        <Reveal>
          <p className="mb-5 font-sans text-xs uppercase tracking-luxe text-gold-light">
            Begin Your Commission
          </p>
          <h2 className="mx-auto max-w-3xl font-serif text-4xl font-light leading-tight text-cream md:text-5xl lg:text-6xl">
            Create something <span className="italic text-gilt">eternal</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl font-sans text-base font-light leading-relaxed text-cream/80">
            Private, by-appointment consultations to design a piece that is
            entirely, and eternally, yours.
          </p>
          <Link to="/contact" className="btn-solid mt-10">
            Request an Appointment
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
